/**
 * Pattern family — loads everything else in the same PATTERN into the
 * product page: the pooled review count, the sets ("complete the table"),
 * and the rail of other pieces.
 *
 * Liquid on a product page cannot query products by tag, so the markup is
 * fetched once from sections/pattern-family.liquid against the pattern's
 * tag-filtered collection URL, then split across up to three mounts.
 *
 * Fails silently and leaves the mounts empty — a broken fetch must never
 * disturb a product page.
 */
(() => {
  const mounts = [...document.querySelectorAll('[data-pattern-family-mount]')];
  if (!mounts.length) return;

  const first = mounts[0];
  const current = first.dataset.currentHandle;
  const isSet = first.dataset.currentIsSet === 'true';
  // 'pattern' (the normal case) or 'article': the product has no pattern tag,
  // so the rail shows the product's article family instead and no sets card.
  let mode = first.dataset.familyMode || 'pattern';
  let pattern = first.dataset.patternName;
  let handle = first.dataset.patternHandle;
  const articleName = first.dataset.articleName;
  const articleHandle = first.dataset.articleHandle;
  if (!pattern || !handle) return;

  const urlFor = (h) => `/collections/all/${encodeURIComponent(h)}?section_id=pattern-family`;

  // Two of the three mounts sit in the buy box, so fetch as soon as the page
  // is idle rather than waiting for a scroll.
  const start = () => load();
  if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 1500 });
  else setTimeout(start, 200);

  function load() {
    fetch(urlFor(handle), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((html) => {
        // Parse only to test for content and to cut out the parts; the
        // markup of each part is written with innerHTML so images decode in
        // the live document (see pattern-switcher.js for why).
        const probe = new DOMParser().parseFromString(html, 'text/html');
        const root = probe.querySelector('[data-pattern-family]');
        if (!root) return;

        // A pattern with nothing else in it (a clearance one-off, say) has no
        // rail and no sets; show the article family instead, once.
        const pieces = Number(root.dataset.pieces || 0);
        const hasSets = !!probe.querySelector('[data-pf-part="sets"] .brp-pf__sets');
        if (mode === 'pattern' && pieces <= 1 && !hasSets && articleHandle && articleHandle !== handle) {
          mode = 'article';
          pattern = articleName;
          handle = articleHandle;
          load();
          return;
        }
        const style = probe.querySelector('.brp-pf style');

        mounts.forEach((mount) => {
          const part = mount.dataset.patternFamilyMount;
          if (part === 'sets' && (isSet || mode === 'article')) return; // a set does not upsell itself; an article family has no sets
          const src = probe.querySelector(`[data-pf-part="${part}"]`);
          if (!src || !src.children.length) return;
          mount.innerHTML = (style ? style.outerHTML : '') + src.innerHTML;
          mount.querySelectorAll('[data-pf-pattern-name]').forEach((el) => { el.textContent = pattern; });
          if (mode === 'article') {
            // No pattern to strip, so show full titles ("Alyce Coffee Mug"),
            // and a heading that does not pretend the tag is a pattern.
            mount.querySelectorAll('[data-pf-heading-text]').forEach((el) => { el.textContent = 'More like this'; });
            mount.querySelectorAll('.brp-pf__name[data-pf-title]').forEach((el) => { el.textContent = el.dataset.pfTitle; });
          }
          const mine = mount.querySelector(`[data-pf-handle="${CSS.escape(current)}"]`);
          if (mine) mine.remove();
          carousel(mount);
          mount.dataset.loaded = 'true';
        });

        track();
      })
      .catch(() => { /* leave the mounts empty */ });
  }

  /**
   * The rail is a Swiper carousel, the same library and the same blue
   * square arrows the mini basket's "Complete your set" rail uses. Swiper
   * is already on every product page for the media gallery; if it is
   * somehow absent the slides fall back to a wrapped static grid.
   */
  const ARROW_PATH = 'M0 416c0 35.3 28.7 64 64 64l320 0c35.3 0 64-28.7 64-64l0-320c0-35.3-28.7-64-64-64L64 32C28.7 32 0 60.7 0 96L0 416zM128 256c0-6.7 2.8-13 7.7-17.6l112-104c7-6.5 17.2-8.2 25.9-4.4s14.4 12.5 14.4 22l0 208c0 9.5-5.7 18.2-14.4 22s-18.9 2.1-25.9-4.4l-112-104c-4.9-4.5-7.7-10.9-7.7-17.6z';
  const arrow = (flip) =>
    `<svg viewBox="0 0 448 512" fill="currentColor" aria-hidden="true"${flip ? ' style="transform:rotate(180deg)"' : ''}><path d="${ARROW_PATH}"></path></svg>`;

  function carousel(mount) {
    const el = mount.querySelector('[data-pf-carousel]');
    if (!el) return;
    const prev = mount.querySelector('[data-pf-prev]');
    const next = mount.querySelector('[data-pf-next]');
    if (prev) prev.innerHTML = arrow(false);
    if (next) next.innerHTML = arrow(true);

    // swiper.js is loaded `defer` by the theme and this module can run
    // before it, so wait for the global rather than testing it once.
    whenSwiper().then((Swiper) => {
      if (!Swiper) { el.classList.add('is-static'); return; }
      init(Swiper);
    });

    function init(Swiper) {
    const count = el.querySelectorAll('.swiper-slide').length;
    new Swiper(el, {
      slidesPerView: 2.5,
      spaceBetween: 8,
      watchOverflow: true,
      breakpoints: {
        640: { slidesPerView: 4 },
        1024: { slidesPerView: Math.min(5, count) }
      },
      navigation: { nextEl: next, prevEl: prev, disabledClass: 'is-disabled' }
    });
    }
  }

  function whenSwiper() {
    return new Promise((resolve) => {
      const started = Date.now();
      (function poll() {
        if (typeof window.Swiper === 'function') return resolve(window.Swiper);
        if (Date.now() - started > 6000) return resolve(null);
        setTimeout(poll, 100);
      })();
    });
  }

  /**
   * Click tracking, same two channels as the pattern switcher: Shopify's
   * custom-event API for pixels, gtag for a readable GA4 report.
   */
  function track() {
    mounts.forEach((mount) => {
      mount.addEventListener('click', (event) => {
        const link = event.target.closest('.brp-pf__link, .brp-pf__set-name, .brp-pf__set-btn');
        if (!link) return;
        const payload = {
          pattern,
          from_handle: current,
          part: mount.dataset.patternFamilyMount,
          to_handle: link.closest('[data-pf-handle], [data-pf-set-handle]')?.dataset.pfHandle
            || link.closest('[data-pf-set-handle]')?.dataset.pfSetHandle || '',
          action: link.classList.contains('brp-pf__set-btn') ? 'add_set' : 'navigate'
        };
        try { window.Shopify?.analytics?.publish?.('pattern_family_click', payload); } catch (e) { /* never block */ }
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'pattern_family_click', { ...payload, transport_type: 'beacon' });
          }
        } catch (e) { /* never block */ }
      });
    });
  }
})();
